import { DateTime, Duration } from 'luxon';
import { HolidayDto } from '../../commonTypes/organization.types';
import { formatToMonthDay } from './dateFormater';
import { LeaveDurationTypeEnum } from '../../commonTypes/leaveRequest.types';

export interface IDateRange {
    startDate: DateTime | null;
    endDate: DateTime | null;
}

export const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const diffDaysWithoutWeekends = ({ startDate, endDate }: IDateRange): number => {
    if (startDate && endDate) {
        let workingDays = 0;
        let currentDate = startDate;

        while (currentDate <= endDate) {
            if (currentDate.weekday <= 5) {
                workingDays++;
            }
            currentDate = currentDate.plus({ days: 1 });
        }

        return workingDays;
    }

    return 0;
};

export const diffDaysWithoutWeekendsAndHolidays = (
    startDate: DateTime,
    endDate: DateTime,
    holidays?: HolidayDto[],
    disabledDays?: DateTime[]
): number => {
    let workingDays = 0;
    let currentDate = startDate.startOf('day');
    const lastDate = endDate.startOf('day');

    while (currentDate <= lastDate) {
        if (currentDate.weekday <= 5 && !isHoliday(currentDate, holidays) && !isInDates(currentDate, disabledDays || [])) {
            workingDays++;
        }
        currentDate = currentDate.plus({ days: 1 }).startOf('day');
    }

    return workingDays;
};

const TWO_DIGITS_LIMIT = 100;
const FOUR_DIGITS_LIMIT = 10000;

export const convertToLuxonDateTime = (input: number, currentDate: DateTime) => {
    let hour: number;
    let minute: number;

    if (input < TWO_DIGITS_LIMIT) {
        hour = input;
        minute = 0;
    } else {
        hour = Math.floor(input / TWO_DIGITS_LIMIT);
        minute = input % TWO_DIGITS_LIMIT;
    }

    return currentDate.set({ hour, minute, second: 0, millisecond: 0 });
};

export const convertColonsToLuxonDuration = (value: string) => {
    //valid inputs example: 12:30, 12:34:33, 5:30, 5:4 ...
    const digits = value.split(':');
    let hour = +digits[0];
    let minute = +digits[1];
    let second = +digits[2] || 0;

    return Duration.fromObject({ hour, minute, second, millisecond: 0 });
};

export const convertNumbersOnlyToLuxonDuration = (value: number) => {
    //valid inputs example: 12, 8.5, 8, 134, 1352, 081252
    let hour = 0;
    let minute = 0;
    let second = 0;

    if (value < TWO_DIGITS_LIMIT) hour = value;
    else if (value < FOUR_DIGITS_LIMIT) {
        hour = Math.floor(value / TWO_DIGITS_LIMIT);
        minute = value % TWO_DIGITS_LIMIT;
    } else {
        hour = Math.floor(value / FOUR_DIGITS_LIMIT);
        minute = Math.floor((value % FOUR_DIGITS_LIMIT) / TWO_DIGITS_LIMIT);
        second = value % TWO_DIGITS_LIMIT;
    }

    return Duration.fromObject({ hour, minute, second, millisecond: 0 });
};

export const hasNonNumericCharacter = (str: string) => {
    const nonNumericPattern = /[^0-9.:]/;
    return nonNumericPattern.test(str);
};

export const dateTimeComparator = (dateA: string, dateB: string) => (DateTime.fromISO(dateA) > DateTime.fromISO(dateB) ? -1 : 1);

/**
 *
 * @param dateISO Date in ISO string format
 * @returns Date string in format MMMM dd.
 */
export const convertIsoToMonthDay = (dateISO: string) => {
    return DateTime.fromISO(dateISO).toFormat('MMMM dd.');
};

/**
 *
 * @param startDate Date in ISO string format
 * @param endDate Date in ISO string format
 * @returns String in format MMMM dd - MMMM dd
 */
export const convertIsoToMonthDayInterval = (startDateIso: string, endDateIso: string) => {
    const from = DateTime.fromISO(startDateIso);
    const to = DateTime.fromISO(endDateIso);

    return `${formatToMonthDay(from)} - ${formatToMonthDay(to)}`;
};

/**
 *
 * @param startDate Date in ISO string format
 * @param endDate Date in ISO string format
 * @param format Format of the dates
 * @returns String in given format
 */
export const convertIsoToMonthDayIntervalWithFormat = (startDateIso: string, endDateIso: string) => {
    const from = DateTime.fromISO(startDateIso);
    const to = DateTime.fromISO(endDateIso);

    return `${formatToMonthDay(from)} - ${formatToMonthDay(to)}`;
};

/**
 *
 * @param startDateIso Date in ISO string format
 * @param endDateIso Date in ISO string format
 * @returns Number of days in interval without weekends
 */
export const convertIsoToDiffDaysWithoutWeekends = (startDateIso: string, endDateIso: string) => {
    const startDate = DateTime.fromISO(startDateIso);
    const endDate = DateTime.fromISO(endDateIso);

    return diffDaysWithoutWeekends({ startDate, endDate });
};

export const convertIsoToDiffDaysWithoutWeekendsAndHolidays = (
    startDateIso: string,
    endDateIso: string,
    holidays: HolidayDto[],
    leaveDurationType: LeaveDurationTypeEnum
) => {
    const startDate = DateTime.fromISO(startDateIso);
    const endDate = DateTime.fromISO(endDateIso);

    const days = diffDaysWithoutWeekendsAndHolidays(startDate, endDate, holidays);
    const isHalfDay = leaveDurationType === LeaveDurationTypeEnum.FIRST_DAY_HALF || leaveDurationType === LeaveDurationTypeEnum.SECOND_DAY_HALF;
    if (isHalfDay) return days / 2;
    else return days;
};

export const isDateToday = (date: DateTime) => {
    const today = DateTime.now();
    return date.hasSame(today, 'day');
};

export const isEventToday = (date: DateTime) => {
    return date.day === DateTime.now().day && date.month === DateTime.now().month;
};

export const isHoliday = (date: DateTime, holidays?: HolidayDto[]) => {
    if (!holidays) {
        return false;
    }

    const formattedDate = date.toISODate();

    return holidays.some(holiday => {
        const holidayDate = DateTime.fromJSDate(new Date(holiday.date));
        return holidayDate.toISODate() === formattedDate;
    });
};

export const isWeekend = (date: DateTime) => {
    return date.weekday === 6 || date.weekday === 7;
};

export const isFutureDate = (date: DateTime) => {
    const today: DateTime = DateTime.now().startOf('day');
    return date.startOf('day') > today;
};

const isInDates = (date: DateTime, dates: DateTime[]): boolean => {
    return dates.some(d => d.toISODate() === date.toISODate());
};
